/**
 * NOTIFICATION SERVICE
 * 
 * Manages notifications for leBusiness:
 * - In-app notifications (bell icon)
 * - Email notifications
 * - Compliance deadline reminders
 * - System messages
 */

import { supabase } from '@/lib/supabase';

export interface Notification {
  id: string;
  user_id: string;
  company_id?: string;
  
  title: string;
  message: string;
  title_ar?: string;
  message_ar?: string;
  
  type: 'compliance' | 'system' | 'invoice' | 'payment' | 'stock' | 'treasury' | 'hr' | 'alert' | 'info' | 'success';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  
  source_app?: string;
  source_entity_type?: string;
  source_entity_id?: string;
  
  action_url?: string;
  action_label?: string;
  action_label_ar?: string;
  
  send_in_app: boolean;
  send_email: boolean;
  send_sms: boolean;
  send_whatsapp: boolean;
  
  is_read: boolean;
  read_at?: string;
  
  created_at: string;
  expires_at?: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  
  enabled: boolean;
  
  email_enabled: boolean;
  email_address?: string;
  sms_enabled: boolean;
  phone_number?: string;
  whatsapp_enabled: boolean;
  whatsapp_number?: string;
  
  compliance_enabled: boolean;
  compliance_email: boolean;
  compliance_sms: boolean;
  
  invoice_enabled: boolean;
  invoice_email: boolean;
  
  payment_enabled: boolean;
  payment_email: boolean;
  
  stock_enabled: boolean;
  stock_email: boolean;
  
  treasury_enabled: boolean;
  treasury_email: boolean;
  
  hr_enabled: boolean;
  hr_email: boolean;
  
  system_enabled: boolean;
  system_email: boolean;
  
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  weekend_notifications: boolean;
  
  compliance_days_before: number[];
  compliance_send_daily_summary: boolean;
  compliance_summary_time: string;
  
  created_at: string;
  updated_at: string;
}

export class NotificationService {
  
  /**
   * Create a notification
   * Automatically gets current user_id if not provided
   */
  static async create(notification: Partial<Notification>): Promise<{ data: Notification | null; error: any }> {
    // Get current user if user_id not provided
    let userId = notification.user_id;
    if (!userId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: new Error('No authenticated user found') };
      }
      userId = user.id;
    }
    
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        ...notification,
        user_id: userId, // Always set user_id
        send_in_app: notification.send_in_app ?? true,
        send_email: notification.send_email ?? false,
        send_sms: notification.send_sms ?? false,
        send_whatsapp: notification.send_whatsapp ?? false,
      })
      .select()
      .single();
    
    return { data, error };
  }
  
  /**
   * Get all notifications for current user
   */
  static async getAll(limit = 50): Promise<{ data: Notification[]; error: any }> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    return { data: data || [], error };
  }
  
  /**
   * Get unread notifications count
   */
  static async getUnreadCount(): Promise<{ count: number; error: any }> {
    const { data, error } = await supabase
      .rpc('get_unread_notification_count');
    
    return { count: data || 0, error };
  }
  
  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<{ error: any }> {
    const { error } = await supabase
      .rpc('mark_notification_read', { notification_id: notificationId });
    
    return { error };
  }
  
  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<{ error: any }> {
    const { error } = await supabase
      .rpc('mark_all_notifications_read');
    
    return { error };
  }
  
  /**
   * Delete a notification
   */
  static async delete(notificationId: string): Promise<{ error: any }> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);
    
    return { error };
  }
  
  /**
   * Get user's notification preferences
   */
  static async getPreferences(): Promise<{ data: NotificationPreferences | null; error: any }> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .single();
    
    return { data, error };
  }
  
  /**
   * Update notification preferences
   */
  static async updatePreferences(
    preferences: Partial<NotificationPreferences>
  ): Promise<{ data: NotificationPreferences | null; error: any }> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert(preferences)
      .select()
      .single();
    
    return { data, error };
  }
  
  /**
   * Subscribe to real-time notifications
   */
  static subscribeToNotifications(
    userId: string,
    callback: (notification: Notification) => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          callback(payload.new as Notification);
        }
      )
      .subscribe();
    
    return {
      unsubscribe: () => {
        supabase.removeChannel(channel);
      },
    };
  }
  
  /**
   * Create a compliance reminder notification
   */
  static async createComplianceReminder(
    userId: string,
    companyId: string,
    obligation: {
      id: string;
      code: string;
      name_fr: string;
      name_ar?: string;
      deadline_date: string;
    },
    daysUntil: number
  ): Promise<{ data: Notification | null; error: any }> {
    
    let priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal';
    let template: string;
    
    if (daysUntil <= 1) {
      priority = 'urgent';
      template = 'compliance_1_day';
    } else if (daysUntil <= 3) {
      priority = 'high';
      template = 'compliance_3_days';
    } else if (daysUntil <= 7) {
      priority = 'normal';
      template = 'compliance_7_days';
    } else {
      priority = 'normal';
      template = 'compliance_7_days';
    }
    
    const title = daysUntil === 1
      ? `DERNIER JOUR: ${obligation.name_fr} demain!`
      : daysUntil <= 3
      ? `Urgent: ${obligation.name_fr} dans ${daysUntil} jours`
      : `Rappel: ${obligation.name_fr} dans ${daysUntil} jours`;
    
    const message = daysUntil === 1
      ? `L'obligation ${obligation.name_fr} (${obligation.code}) doit être déposée DEMAIN. Déclarez maintenant!`
      : `L'obligation ${obligation.name_fr} (${obligation.code}) doit être déposée avant le ${obligation.deadline_date}.`;
    
    return this.create({
      user_id: userId,
      company_id: companyId,
      type: 'compliance',
      priority,
      title,
      message,
      title_ar: obligation.name_ar,
      source_app: 'compliance',
      source_entity_type: 'obligation',
      source_entity_id: obligation.id,
      action_url: '/apps/compliance',
      action_label: 'Voir les obligations',
      action_label_ar: 'عرض الالتزامات',
      send_in_app: true,
      send_email: daysUntil <= 3, // Email for urgent ones
      expires_at: obligation.deadline_date,
    });
  }
  
  /**
   * Create invoice notification
   */
  static async createInvoiceNotification(
    userId: string,
    invoiceId: string,
    type: 'created' | 'paid' | 'overdue',
    invoiceNumber: string,
    amount: number
  ): Promise<{ data: Notification | null; error: any }> {
    
    const titles = {
      created: `Facture ${invoiceNumber} créée`,
      paid: `✓ Facture ${invoiceNumber} payée`,
      overdue: `⚠️ Facture ${invoiceNumber} en retard`,
    };
    
    const messages = {
      created: `Nouvelle facture créée pour ${amount.toLocaleString('fr-DZ')} DZD`,
      paid: `La facture a été payée. Montant: ${amount.toLocaleString('fr-DZ')} DZD`,
      overdue: `La facture est en retard. Montant dû: ${amount.toLocaleString('fr-DZ')} DZD`,
    };
    
    const priorities = {
      created: 'normal' as const,
      paid: 'normal' as const,
      overdue: 'high' as const,
    };
    
    return this.create({
      user_id: userId,
      type: 'invoice',
      priority: priorities[type],
      title: titles[type],
      message: messages[type],
      source_app: 'invoices',
      source_entity_type: 'invoice',
      source_entity_id: invoiceId,
      action_url: `/invoices/${invoiceId}`,
      action_label: 'Voir la facture',
      send_in_app: true,
      send_email: type === 'overdue',
    });
  }
  
  /**
   * Create stock alert
   */
  static async createStockAlert(
    userId: string,
    productId: string,
    productName: string,
    currentStock: number,
    minStock: number
  ): Promise<{ data: Notification | null; error: any }> {
    return this.create({
      user_id: userId,
      type: 'stock',
      priority: 'high',
      title: `⚠️ Stock faible: ${productName}`,
      message: `Stock actuel: ${currentStock} (minimum: ${minStock}). Réapprovisionnement nécessaire.`,
      source_app: 'stock',
      source_entity_type: 'product',
      source_entity_id: productId,
      action_url: `/stock/products/${productId}`,
      action_label: 'Voir le produit',
      send_in_app: true,
      send_email: true,
    });
  }
  
  /**
   * Create system notification
   */
  static async createSystemNotification(
    userId: string,
    title: string,
    message: string,
    priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal'
  ): Promise<{ data: Notification | null; error: any }> {
    return this.create({
      user_id: userId,
      type: 'system',
      priority,
      title,
      message,
      source_app: 'system',
      send_in_app: true,
      send_email: priority === 'urgent',
    });
  }
}
